import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { answerHumanStep, newClientKey } from "../../../api/threads/threadsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { includedMembers } from "../datasets/acceptedResults";
import MemberProblems from "../datasets/MemberProblems";
import RefusedReleaseChecks from "../datasets/RefusedReleaseChecks";
import { loadReleaseContext, type ReleaseContext } from "./releaseContext";
import { partComplete, releaseRequestOutput, requestWithPart, type ReleaseDraft, type ReleasePart, type ReleaseRequest } from "./releaseRequest";
import ReleaseRequestSummary from "./ReleaseRequestSummary";
import { MetadataStep, WhatStep, WhereStep } from "./ReleaseSteps";

/**
 * The view of one release step in its Thread (ADR-0026 slice 3b, Johannes 27 Oct): the
 * release dialog's form for that part, filled with what the earlier steps chose and, for the
 * metadata, with the last release's. Sending answers the step with the whole request.
 */
export default function ReleasePartAnswer({
  projectId,
  threadId,
  answersEntry,
  part,
  earlier,
}: {
  projectId: string;
  threadId: string;
  answersEntry: number;
  part: ReleasePart;
  /** The request so far, from the step's input; `null` for the first part. */
  earlier: ReleaseRequest | null;
}) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => loadReleaseContext(projectId, signal), [projectId]);
  const context = useLoad(load);
  if (part !== "what" && earlier === null) {
    return <p className="app-text-muted text-sm">{t("data.release.step.missingRequest")}</p>;
  }
  if (context.state.status !== "loaded") {
    return <LoadStatus state={context.state} loadingLabel={t("common.loading")} onRetry={context.reload} />;
  }
  return <LoadedPart projectId={projectId} threadId={threadId} answersEntry={answersEntry} part={part} earlier={earlier} context={context.state.value} />;
}

function LoadedPart({
  projectId,
  threadId,
  answersEntry,
  part,
  earlier,
  context,
}: {
  projectId: string;
  threadId: string;
  answersEntry: number;
  part: ReleasePart;
  earlier: ReleaseRequest | null;
  context: ReleaseContext;
}) {
  const { t } = useTranslation();
  const last = context.series?.metadata;
  const [draft, setDraft] = useState<ReleaseDraft>(() => ({
    modelVersion: context.versions[0]?.commit ?? null,
    overrides: new Map(),
    title: earlier?.metadata?.title ?? last?.title ?? "",
    licence: earlier?.metadata?.licence ?? last?.licence ?? "CC-BY-4.0",
    description: earlier?.metadata?.description ?? last?.description ?? "",
    toResearchSpace: true,
  }));
  const [clientRequestId] = useState(newClientKey);
  const { state, submit } = useSubmission();
  const change = (update: Partial<ReleaseDraft>) => { setDraft((previous) => ({ ...previous, ...update })); };
  const members = includedMembers(context.choices, draft.overrides);
  const canPublish = context.researchSpace.available && context.researchSpace.publishes;
  // Where the project cannot publish, the release stays in DMW whatever the box says.
  const answered = part === "where" ? { ...draft, toResearchSpace: canPublish && draft.toResearchSpace } : draft;
  const request = requestWithPart(part, answered, context.choices, earlier, context.series?.latestVersion ?? 0, last?.keywords ?? []);
  const settled = state.status === "submitting" || state.status === "succeeded";

  const send = () => {
    if (request === null) return;
    submit(async () => {
      await answerHumanStep(threadId, { answersEntry, comment: null, choice: null, result: await releaseRequestOutput(request), workspaceFile: null, clientRequestId });
    });
  };

  return (
    <div className="space-y-3">
      {earlier !== null ? <ReleaseRequestSummary projectId={projectId} request={earlier} /> : null}
      {part === "what" ? <WhatStep draft={draft} onChange={change} versions={context.versions} choices={context.choices} /> : null}
      {part === "metadata" ? <MetadataStep draft={draft} onChange={change} firstRelease={context.series === null} /> : null}
      {part === "where" ? <WhereStep draft={draft} onChange={change} available={context.researchSpace.available} canPublish={canPublish} /> : null}
      {state.status === "failed" ? (
        <div className="space-y-2">
          <ErrorNotice error={state.error} compact={true} />
          <MemberProblems error={state.error} />
          <RefusedReleaseChecks error={state.error} />
        </div>
      ) : null}
      <div className="flex justify-end">
        <AppButton variant="primary" size="sm" disabled={settled || request === null || !partComplete(part, draft, members)} onClick={send}>
          {part === "what" ? t("data.release.step.sendWhat", { count: members.length }) : t("threads.humanStep.send")}
        </AppButton>
      </div>
    </div>
  );
}
